import { Component, Input, OnInit } from '@angular/core';
import {
    closedAppointmentStatuses,
    Complaint,
    ComplaintAppointment,
    ComplaintPosition,
    DocumentAttachment,
    PersonsService,
} from '@icc/b2b/shared';
import { ComplaintService } from '../complaint.service';
import { ConfigService, UserService } from '@icc/shared';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
    selector: 'icc-complaint-appointment',
    templateUrl: './complaint-appointment.component.html',
    styleUrls: ['./complaint-appointment.component.scss'],
})
export class ComplaintAppointmentComponent implements OnInit {
    @Input() appointment!: ComplaintAppointment;
    @Input() document!: Complaint;
    @Input() index = 0;
    @Input() attachments: DocumentAttachment[] = [];
    @Input() preview = false;
    @Input() positionsWorkflowsGroups: Map<number, ComplaintPosition[]> = new Map<
        number,
        ComplaintPosition[]
    >();

    complaints: Complaint[] = [];

    assignedCompanyName: string | undefined;

    canEdit = true;
    canEditProtocol = true;
    canRemove = true;

    isServiceman: boolean;

    activeUserCompanyId: number;

    appointmentAttachments: DocumentAttachment[] = [];

    constructor(
        private complaintService: ComplaintService,
        private personsService: PersonsService,
        private configService: ConfigService,
        private permissionsService: NgxPermissionsService
    ) {
        this.activeUserCompanyId = Number(this.configService?.user?.user?.ActiveCompany);
    }

    async ngOnInit(): Promise<void> {
        await this.complaintService.workflowsLoaded;
        await this.determineEditAccess();
        if (
            this.appointment.assignedCompanyId === this.document.authorizedRetailer?.companyId ||
            this.appointment.assignedCompanyId === this.document.authorizedRetailerCompanyId
        ) {
            this.assignedCompanyName = this.document.authorizedRetailer?.companyName;
        } else if (this.appointment.assignedCompanyId === this.document.distributor?.companyId) {
            this.assignedCompanyName = this.document.distributor?.companyName;
        } else if (this.appointment.assignedCompanyId === this.document.manufacturer?.companyId) {
            this.assignedCompanyName = this.document.manufacturer?.companyName;
        }
    }

    private async determineEditAccess() {
        const isAssignedToUserCompany = this.appointment.assignedCompanyId === this.activeUserCompanyId;
        const isDocumentAssignedToUserType = this.document.assignedTo &&
            this.document.assignedTo.startsWith(this.complaintService.getUserType());
        const hasManageManufacturerPermission = await this.permissionsService.hasPermission('complaints-manage-manufacturer');
        const isNotClosedAppointment = !closedAppointmentStatuses.some((status) => status.value === this.appointment.status);
        const statusAllowsEdit = this.currentStatusAllowEdit();

        // Check if user's company is the complaint creator in internal processes
        let isComplaintAuthorCompany = false;
        if (this.positionsWorkflowsGroups?.size === 1) {
            const workflowId = this.positionsWorkflowsGroups.keys().next().value;
            if (workflowId !== undefined) {
                const workflow = this.complaintService.workflows.find(w => +w.id === +workflowId);
                const userCompanyId = this.configService.user?.company?.id;
                
                if (workflow?.complaintCreator === 'authorized_retailer' && userCompanyId && this.document.authorizedRetailerCompanyId) {
                    isComplaintAuthorCompany = +this.document.authorizedRetailerCompanyId === +userCompanyId;
                } else if (workflow?.complaintCreator === 'distributor' && userCompanyId && this.document.distributorCompanyId) {
                    isComplaintAuthorCompany = +this.document.distributorCompanyId === +userCompanyId;
                }
            }
        }

        const commonConditions = isAssignedToUserCompany &&
            (isDocumentAssignedToUserType || hasManageManufacturerPermission) &&
            isNotClosedAppointment &&
            statusAllowsEdit &&
            !isComplaintAuthorCompany;

        // canEdit - for appointment editing (add/edit/remove appointment)
        // requires complaints-manage permission
        this.canEdit =
            (await this.permissionsService.hasPermission('complaints-manage')) &&
            commonConditions;

        // canEditProtocol - for protocol editing only
        // requires complaints-manage OR complaints-manage-protocol permission
        this.canEditProtocol =
            (await this.permissionsService.hasPermission('complaints-manage') ||
             await this.permissionsService.hasPermission('complaints-manage-protocol')) &&
            commonConditions;

        this.canRemove = this.canEdit && !this.attachments.some((attachment) => attachment.attachmentType === 'appointmentDeliveryNote');
    }

    async ngOnChanges(): Promise<void> {
        this.appointmentAttachments = this.attachments.filter(
            (attachment) =>
                (attachment.attachmentType === 'appointmentAttachment' ||
                    attachment.attachmentType === 'appointmentDeliveryNote' && (this.complaintService.getUserType() === 'manufacturer' || this.complaintService.getUserType() === 'distributor')) &&
                attachment.appointmentId === this.appointment.uuid
        );
        await this.determineEditAccess();
    }

    removeAppointment() {
        this.complaintService.deleteAppointment(this.appointment);
    }

    editAppointment() {
        this.complaintService.editAppointment(
            this.document,
            this.appointment,
            this.attachments,
            true,
            this.positionsWorkflowsGroups
        );
    }

    editProtocol() {
        this.complaintService.editProtocol(
            this.document,
            this.appointment,
            this.attachments,
            this.positionsWorkflowsGroups,
            true,
            false
        );
    }

    viewProtocol() {
        this.complaintService.editProtocol(
            this.document,
            this.appointment,
            this.attachments,
            this.positionsWorkflowsGroups,
            false,
            true
        );
    }

    getWorkerName(id: number): string {
        return this.personsService.getWorkerByUserId(id)?.person.full_formal_name ?? '';
    }

    getAttachment(type: DocumentAttachment['attachmentType'], uuid: string) {
        return this.attachments.find(
            (attachment) => attachment.attachmentType === type && attachment.uuid === uuid
        );
    }

    showAppointmentNote() {
        this.complaintService.showAppointmentNote(this.appointment.comment);
    }

    async checkAccess() {
        this.canEdit =
            this.appointment.assignedCompanyId === this.activeUserCompanyId &&
            ((this.document.assignedTo &&
                this.document.assignedTo.startsWith(this.complaintService.getUserType())) ||
                (await this.permissionsService.hasPermission('complaints-manage-manufacturer')));
    }

    getStatus(statusId: number) {
        return this.complaintService.getStatus(statusId);
    }

    private currentStatusAllowEdit(): boolean {
        if (!this.document) {
            return false;
        }

        const userType = this.complaintService.getUserType();
        const statusId = this.complaintService.getDocumentStatusForUser(this.document, userType);

        if (statusId === 0 || isNaN(statusId)) {
            return true;
        }

        const status = this.getStatus(statusId);
        return status?.allowEdit ?? false;
    }
}
